import React, { useState } from 'react';
import { TextField, Button, Typography, Stack, Link, Alert, InputAdornment, IconButton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import AuthShell from '../../components/auth/AuthShell';

const destFor = (role) =>
  role === 'master' || role === 'admin' ? '/master/dashboard' : '/user/cabinet';

export default function Login() {
  const { t } = useTranslation();
  const [mode, setMode] = useState('password'); // 'password' | 'otp'
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // OTP-вхід
  const [phone, setPhone] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [devCode, setDevCode] = useState('');
  const [code, setCode] = useState('');

  const afterLogin = (user) => {
    if (user) localStorage.setItem('user', JSON.stringify(user));
    window.location.assign(destFor(user?.role));
  };

  // --- Вхід за паролем (email або телефон) ---
  const handlePassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const resp = await api.login(identifier, password);
      afterLogin(resp.user || resp);
    } catch (err) {
      setError(err.message || t('auth.invalidCredentials'));
      setLoading(false);
    }
  };

  // --- Вхід за кодом: надіслати код ---
  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Бекенд перевіряє наявність акаунта ДО надсилання SMS (намір 'login')
      const res = await api.requestOtp(phone, 'login');
      setDevCode(res.devCode || '');
      setCodeSent(true);
    } catch (err) {
      setError(err.status === 404 ? t('auth.notRegistered') : (err.message || t('auth.couldNotSendCode')));
    } finally {
      setLoading(false);
    }
  };

  // --- Вхід за кодом: підтвердити ---
  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const v = await api.verifyOtp({ phone, code });
      afterLogin(v.user);
    } catch (err) {
      setError(err.message || t('auth.invalidCode'));
      setLoading(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setError('');
    setCodeSent(false);
    setCode('');
  };

  return (
    <AuthShell title={t('auth.loginTitle')}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {mode === 'password' && (
        <form onSubmit={handlePassword}>
          <Stack spacing={2}>
            <TextField
              label={t('auth.emailOrPhone')}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoComplete="username"
              fullWidth
            />
            <TextField
              label={t('common.password')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                      onClick={() => setShowPassword((v) => !v)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" variant="contained" disabled={loading} fullWidth>
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
          </Stack>
          <Typography variant="body2" sx={{ mt: 2 }}>
            <Link component="button" type="button" onClick={() => switchMode('otp')}>
              {t('auth.loginByCode')}
            </Link>
          </Typography>
        </form>
      )}

      {mode === 'otp' && !codeSent && (
        <form onSubmit={handleSendCode}>
          <Stack spacing={2}>
            <TextField label={t('common.phone')} value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth />
            <Button type="submit" variant="contained" disabled={loading} fullWidth>
              {loading ? t('common.sending') : t('auth.sendCode')}
            </Button>
          </Stack>
          <Typography variant="body2" sx={{ mt: 2 }}>
            <Link component="button" type="button" onClick={() => switchMode('password')}>
              {t('auth.loginByPassword')}
            </Link>
          </Typography>
        </form>
      )}

      {mode === 'otp' && codeSent && (
        <form onSubmit={handleVerify}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t('auth.codeSentTo', { phone })}
            </Typography>
            {devCode && <Alert severity="warning">{t('auth.devCode', { code: devCode })}</Alert>}
            <TextField
              label={t('auth.smsCode')}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric' }}
              autoFocus
              fullWidth
            />
            <Button type="submit" variant="contained" disabled={loading} fullWidth>
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
          </Stack>
        </form>
      )}

      <Typography variant="body2" sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        {t('auth.noBusinessAccount')}{' '}
        <Link component={RouterLink} to="/register-business">{t('auth.registerBusinessLink')}</Link>
      </Typography>
    </AuthShell>
  );
}
